import { Injectable } from "@angular/core";
import { RemoteGatewayFactory } from "../../gateways/remote-gateway-factory";
import { PagingRequest } from "../../models/domains/paginator/paging-request";
import { Practitioner } from "../../models/domains/pbm/practitioner-management/practitioner";
import { PractitionerSpecialty } from "../../models/domains/pbm/practitioner-management/practitioner-specialty";
import { PractitionerStatus } from "../../models/domains/pbm/practitioner-management/practitioner-status";
import { SearchPractitionerRequest } from "../../models/domains/pbm/practitioner-management/search-practitioner.request";
import { SearchPractitionerResult } from "../../models/domains/pbm/practitioner-management/search-practitioner.result";
import { MdmBackendUrl } from "../../models/domains/urls/mdm-back-end.url";
import { PractitionerSpecialtiesParser } from "../parsers/pbm/practitioner-management/practitioner-specialties.parser";
import { PractitionerStatusParser } from "../parsers/pbm/practitioner-management/practitioner-status.parser";
import { SearchPractitionerByKeyParser } from "../parsers/pbm/practitioner-management/search-practitioner-by-key.parser";
import { SearchPractitionersParser } from "../parsers/pbm/practitioner-management/search-practitioners.parser";

const headers = { "Content-Type": "application/json" };

@Injectable()
export class PractitionerManagementRepository {
  constructor(
    private remoteGatewayFactory: RemoteGatewayFactory,
    private searchPractitionersParser: SearchPractitionersParser,
    private serachPractitionerByKeyParser: SearchPractitionerByKeyParser,
    private practitionerSpecialtiesParser: PractitionerSpecialtiesParser,
    private practitionerStatusParser: PractitionerStatusParser,
  ) {}

  public async searchPractitioners(request: SearchPractitionerRequest): Promise<SearchPractitionerResult> {
    const body = JSON.stringify({
      StartIndex: request.getStartIndex(),
      TotalPerPages: request.getTotalPerPage(),
      FilterText: request.getFilterText(),
    });
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.postWithHeaders(new MdmBackendUrl("/api/mdm/practitioners/fetch"), body, headers);
      const searchResult: SearchPractitionerResult = this.searchPractitionersParser.parse(data);
      searchResult.setRequest(request);
      return Promise.resolve(searchResult);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async searchPractitionerByKey(key: string): Promise<Practitioner> {
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.getWithHeaders(new MdmBackendUrl(`/api/mdm/practitioners/${key}`), headers);
      return Promise.resolve(this.serachPractitionerByKeyParser.parse(data));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async deletePractitioner(practitioner: Practitioner): Promise<void> {
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.deleteWithHeaders(new MdmBackendUrl(`/api/mdm/practitioners/${practitioner.getKey()}`), headers);
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async registerPractitioner(practitioner: Practitioner): Promise<string> {
    const body = {
      PractitionerRegistration: {
        Registration: practitioner.getRegistration(),
        State: {
          Value: practitioner.getStateAcronym().getValue(),
        },
      },
      Name: practitioner.getName(),
      PractitionerStatus: {
        Key: { Value: practitioner.getStatus().getKey() },
        Description: practitioner.getStatus().getDescription(),
      },
      RegistrationType: "Principal",
      Specialties: practitioner.getSpecialties().map((item) => {
        return {
          PractitionerSpecialtyKey: {
            Value: item.getKey(),
          },
          Description: item.getDescription(),
        };
      }),
    };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.postWithHeaders(new MdmBackendUrl("/api/mdm/practitioners/add"), body, headers);
      return Promise.resolve(data.Value);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async editPractitioner(practitioner: Practitioner): Promise<void> {
    const body = {
      PractitionerRegistration: {
        Registration: practitioner.getRegistration(),
        State: {
          Value: practitioner.getStateAcronym().getValue(),
        },
      },
      Name: practitioner.getName(),
      PractitionerStatus: {
        Key: { Value: practitioner.getStatus().getKey() },
        Description: practitioner.getStatus().getDescription(),
      },
      RegistrationType: "Principal",
      Specialties: practitioner.getSpecialties().map((item) => {
        return {
          PractitionerSpecialtyKey: {
            Value: item.getKey(),
          },
          Description: item.getDescription(),
        };
      }),
    };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.putWithHeaders(new MdmBackendUrl(`/api/mdm/practitioners/${practitioner.getKey()}`), body, headers);
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getSpecialties(request: PagingRequest): Promise<PractitionerSpecialty[]> {
    const body = JSON.stringify({
      StartIndex: request.getStart(),
      TotalPerPages: request.getEnd(),
      FilterText: request.getFilterText(),
    });
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.postWithHeaders(new MdmBackendUrl("/api/mdm/practitioners/specialties"), body, headers);
      return Promise.resolve(this.practitionerSpecialtiesParser.parse(data.Data));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getAllPractitionerStatus(): Promise<PractitionerStatus[]> {
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data: any = await remoteGateway.getWithHeaders(new MdmBackendUrl("/api/mdm/practitioners/status"), headers);
      return Promise.resolve(this.practitionerStatusParser.parse(data));
    } catch (error) {
      return Promise.reject(error);
    }
  }
}
